/**
 * LayerCanvas — moteur de calques 100 % local (HTML5 Canvas).
 * Dessine les positions patrimoniales PAR-DESSUS la carte : l'API de tuiles
 * ne reçoit JAMAIS ces données (séparation stricte tuiles ↔ patrimoine).
 * Hit-test via l'événement click Leaflet (le canvas reste pointer-events:none).
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import { eur } from './deviceVault';

export const LayerCanvas = forwardRef(({ assets, color, onAssetClick }, ref) => {
  const canvasRef = useRef(null);
  const map = useMap();

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !map) return;
    const size = map.getSize();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size.x * dpr;
    canvas.height = size.y * dpr;
    canvas.style.width = `${size.x}px`;
    canvas.style.height = `${size.y}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.x, size.y);
    const maxV = Math.max(...assets.map((a) => a.value), 1);
    for (const a of assets) {
      const p = map.latLngToContainerPoint([a.lat, a.lng]);
      if (p.x < -60 || p.y < -60 || p.x > size.x + 60 || p.y > size.y + 60) continue;
      const r = 7 + 9 * Math.sqrt(a.value / maxV);
      const glow = ctx.createRadialGradient(p.x, p.y, 1, p.x, p.y, r * 2.4);
      glow.addColorStop(0, `${color}CC`);
      glow.addColorStop(0.5, `${color}44`);
      glow.addColorStop(1, `${color}00`);
      ctx.fillStyle = glow;
      ctx.beginPath(); ctx.arc(p.x, p.y, r * 2.4, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(p.x, p.y, r * 0.42, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 1.2;
      ctx.stroke();
      ctx.font = '600 10.5px Inter, sans-serif';
      ctx.textAlign = 'center';
      const label = eur(a.value);
      const w = ctx.measureText(label).width + 12;
      ctx.fillStyle = 'rgba(10,10,14,0.78)';
      ctx.beginPath();
      ctx.roundRect(p.x - w / 2, p.y - r - 22, w, 16, 8);
      ctx.fill();
      ctx.fillStyle = '#E5E7EB';
      ctx.fillText(label, p.x, p.y - r - 10);
    }
  }, [assets, color, map]);

  useMapEvents({
    move: draw,
    zoom: draw,
    resize: draw,
    click: (e) => {
      const cp = map.latLngToContainerPoint(e.latlng);
      let best = null, bestD = 26;
      for (const a of assets) {
        const p = map.latLngToContainerPoint([a.lat, a.lng]);
        const d = Math.hypot(p.x - cp.x, p.y - cp.y);
        if (d < bestD) { bestD = d; best = a; }
      }
      onAssetClick?.(best);
    },
  });

  useEffect(() => { draw(); }, [draw]);
  useImperativeHandle(ref, () => ({
    snapshot: () => canvasRef.current?.toDataURL('image/png') || null,
  }), []);

  return (
    <canvas ref={canvasRef} data-testid="layer-canvas"
            className="absolute inset-0 pointer-events-none"
            style={{ zIndex: 640 }} />
  );
});
